import { useQuery } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment-timezone';
import pluralize from 'pluralize';
import { useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';

import { gengql } from '@/__generated__';
import { ConsultationEdge } from '@/__generated__/graphql';
import { getUser, getUserName } from '@/consultation';
import { isCallType } from '@/expertrequest/store';
import { RootState } from '@/store';

import Link from '../Link';
import Picture from '../Picture';

const GET_CONSULTATIONS = gengql(/* GraphQL */ `
  query fetchStartingConsultations($userContext: String!) {
    startingConsultations(user_context: $userContext) {
      pageInfo {
        hasNextPage
      }
      edges {
        cursor
        node {
          id
          html_url
          starts_at
          expert_request {
            id
            er_type
          }
          requester {
            id
            name
            first_name
            last_name
            html_url
            picture_url
          }
          requester_name
          requester_identifier
          expert {
            id
            name
            first_name
            last_name
            html_url
            picture_url
          }
          expert_name
          expert_identifier
          state
        }
      }
    }
  }
`);

type Consultation = ConsultationEdge['node'];

const connector = connect((state: RootState) => ({
  viewer: state.viewer,
  call: state.call, // KT: this isn't really used anymore for Zoom.
  userContext: state.ui.userContext,
}));

const ConsultationsStarting = ({ userContext, viewer, call }: ConnectedProps<typeof connector>) => {
  const { data } = useQuery(GET_CONSULTATIONS, { variables: { userContext } });

  const [consultationDialogClosed, setConsultationDialogClosed] = useState<Record<string, boolean>>(
    {}
  );

  const closeConsultationAlert = (id: string) => {
    setConsultationDialogClosed((prevState) => ({
      ...prevState,
      [id]: true,
    }));
  };

  const edges = data?.startingConsultations?.edges || [];
  const consultations = edges.map((e) => e?.node).filter((c) => !!c);
  const filteredConsultations = consultations.filter(
    (c) =>
      !consultationDialogClosed[c.id] &&
      c.id !== call.consultationId &&
      (!c.expert_request || (c.expert_request.er_type && isCallType(c.expert_request.er_type)))
  );

  if (filteredConsultations.length === 0) return null;

  return (
    <div
      className={`${filteredConsultations.length >= 4 ? 'grid grid-cols-2' : 'flex flex-col'} mb-16 gap-8`}
    >
      {filteredConsultations.map((c) => {
        const diff = moment(c.starts_at).diff(moment());
        const minutes = moment.duration(diff).minutes();

        const user = getUser(viewer, userContext, c as Consultation, false);
        const userName = getUserName(viewer, userContext, c as Consultation, false);

        const isInProgress = minutes <= 0;
        const timeLabel = isInProgress
          ? 'In progress'
          : `Starts in ${pluralize('minute', minutes, true)}`;

        return (
          <div
            key={c.id}
            className={`flex items-center gap-12 rounded-md border py-10 pl-16 pr-12 ${isInProgress ? 'border-teal-800' : 'border-surface-border'} ${isInProgress ? 'bg-brand-light dark:bg-teal-900/30' : 'bg-surface-card'} shadow-5`}
          >
            <Picture user={user} size={36} link={false} />

            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-14 font-semibold text-on-surface">Call with {userName}</span>
              <span
                className={`text-12 ${isInProgress ? 'font-semibold text-brand-primary' : 'font-normal text-on-surface-muted'}`}
              >
                {timeLabel}
              </span>
            </div>

            <Tooltip title="Join this call" arrow>
              <span className="inline-flex">
                <Link
                  to={`${c.html_url}?join_call=true`}
                  className="inline-flex items-center gap-5 whitespace-nowrap rounded-sm bg-brand-primary px-12 py-6 text-14 font-semibold leading-snug text-white no-underline"
                >
                  <PhoneIcon sx={{ fontSize: 14 }} />
                  Join Call
                </Link>
              </span>
            </Tooltip>

            <Tooltip title="Dismiss" arrow>
              <IconButton
                size="small"
                onClick={() => closeConsultationAlert(c.id)}
                sx={{ p: '4px' }}
              >
                <CloseIcon sx={{ fontSize: 16, color: 'var(--color-on-surface-muted)' }} />
              </IconButton>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

export default connector(ConsultationsStarting);
